import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Divider,
  Grid,
  Box,
  LinearProgress,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
  Button,
  ButtonGroup
} from '@mui/material';
import {
  DirectionsBoat as VesselIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { getVessels } from '../firebase/services/vesselService';
import { getAllReservations } from '../firebase/services/reservationService';
import { getMarinaSettings } from '../firebase/services/marinaSettingsService';
import { Vessel, Reservation, BusinessHours } from '../models/types';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  startOfDay, 
  endOfDay, 
  subDays, 
  eachDayOfInterval, 
  parseISO 
} from 'date-fns';

type TimeFrame = 'today' | 'week' | 'month' | 'season' | 'last7' | 'last30';

interface VesselUtilizationData {
  vessel: Vessel;
  dailyRentableHours: number;
  periodRentableHours: number;
  periodReservedHours: number;
  utilizationRate: number;
  reservationCount: number;
  revenue: number;
}

interface VesselTypeUtilizationData {
  vesselType: string;
  vesselCount: number;
  totalPeriodRentableHours: number;
  totalPeriodReservedHours: number;
  utilizationRate: number;
  totalReservations: number;
  totalRevenue: number;
  vessels: VesselUtilizationData[];
}

interface VesselUtilizationCardProps {
  onVesselClick?: (vesselId: string) => void;
}

const VesselUtilizationCard: React.FC<VesselUtilizationCardProps> = ({ onVesselClick }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [utilizationData, setUtilizationData] = useState<VesselUtilizationData[]>([]);
  const [vesselTypeData, setVesselTypeData] = useState<VesselTypeUtilizationData[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [businessHoursWarning, setBusinessHoursWarning] = useState(false);
  const [timeFrame, setTimeFrame] = useState<TimeFrame>('week');
  const [marinaSettings, setMarinaSettings] = useState<any>(null);
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: startOfWeek(new Date(), { weekStartsOn: 1 }),
    end: endOfWeek(new Date(), { weekStartsOn: 1 })
  });

  useEffect(() => {
    updateDateRange();
  }, [timeFrame]);

  useEffect(() => {
    loadUtilizationData();
  }, [dateRange]);

  const updateDateRange = () => {
    const now = new Date();
    let start: Date;
    let end: Date;

    switch (timeFrame) {
      case 'today':
        start = startOfDay(now);
        end = endOfDay(now);
        break;
      case 'week':
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'month':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'season':
        if (marinaSettings) {
          const currentYear = now.getFullYear();
          const seasonStart = marinaSettings.seasonStartDate || '05-01';
          const seasonEnd = marinaSettings.seasonEndDate || '09-30';
          
          // Parse season dates (handle both YYYY-MM-DD and MM-DD formats)
          let startMonth, startDay, endMonth, endDay;
          
          if (seasonStart.includes('-') && seasonStart.split('-').length === 3) {
            const [year, month, day] = seasonStart.split('-').map(Number);
            startMonth = month;
            startDay = day;
          } else {
            const [month, day] = seasonStart.split('-').map(Number);
            startMonth = month;
            startDay = day;
          }
          
          if (seasonEnd.includes('-') && seasonEnd.split('-').length === 3) {
            const [year, month, day] = seasonEnd.split('-').map(Number);
            endMonth = month;
            endDay = day;
          } else {
            const [month, day] = seasonEnd.split('-').map(Number);
            endMonth = month;
            endDay = day;
          }
          
          start = new Date(currentYear, startMonth - 1, startDay);
          end = new Date(currentYear, endMonth - 1, endDay);
          
          // If season end is before season start, it spans across years
          if (end < start) {
            // Determine which year we should be looking at based on current date
            if (now.getMonth() + 1 >= startMonth || now.getMonth() + 1 <= endMonth) {
              // We're in season, use current year for start, next year for end if needed
              if (now.getMonth() + 1 >= startMonth) {
                end = new Date(currentYear + 1, endMonth - 1, endDay);
              } else {
                start = new Date(currentYear - 1, startMonth - 1, startDay);
              }
            }
          }
        } else {
          // Fallback to default season dates if marina settings not loaded
          start = new Date(now.getFullYear(), 4, 1); // May 1st
          end = new Date(now.getFullYear(), 8, 30); // September 30th
        }
        break;
      case 'last7':
        start = subDays(now, 6);
        end = endOfDay(now);
        break;
      case 'last30':
        start = subDays(now, 29);
        end = endOfDay(now);
        break;
      default:
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfWeek(now, { weekStartsOn: 1 });
    }

    setDateRange({ start, end });
  };

  const getTimeFrameLabel = (): string => {
    switch (timeFrame) {
      case 'today':
        return 'Today';
      case 'week':
        return `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d')}`;
      case 'month':
        return format(dateRange.start, 'MMMM yyyy');
      case 'season':
        return `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d, yyyy')}`;
      case 'last7':
        return 'Last 7 days';
      case 'last30':
        return 'Last 30 days';
      default:
        return `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d')}`;
    }
  };

  const getTimeFrameDisplayName = (): string => {
    switch (timeFrame) {
      case 'today':
        return 'Daily';
      case 'week':
        return 'Weekly';
      case 'month':
        return 'Monthly';
      case 'season':
        return 'Seasonal';
      case 'last7':
        return '7-Day';
      case 'last30':
        return '30-Day';
      default:
        return 'Weekly';
    }
  };

  const calculateDailyRentableHours = (marinaSettings: any): number => {
    // Calculate average daily rentable hours across the week
    const businessHours = getActiveBusinessHours(marinaSettings);
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    let totalHours = 0;
    let activeDays = 0;

    days.forEach(day => {
      const dayHours = businessHours[day as keyof BusinessHours];
      if (dayHours && !dayHours.isClosed && dayHours.openTime && dayHours.closeTime) {
        try {
          const openTimeMinutes = convertTimeToMinutes(dayHours.openTime);
          const closeTimeMinutes = convertTimeToMinutes(dayHours.closeTime);
          if (closeTimeMinutes > openTimeMinutes) {
            totalHours += (closeTimeMinutes - openTimeMinutes) / 60; // Convert minutes to hours
            activeDays++;
          }
        } catch (error) {
          console.warn(`Error parsing ${day} hours:`, error);
        }
      }
    });

    return activeDays > 0 ? totalHours / activeDays : 8; // Default to 8 hours if no hours set
  };

  // Use the exact same time conversion utility as the reservation system
  const convertTimeToMinutes = (timeStr: string): number => {
    try {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    } catch (error) {
      throw error;
    }
  };

  // Get business hours (copied from reservationService.ts)
  const getActiveBusinessHours = (marinaSettings: any) => {
    // Parse season dates correctly handling both YYYY-MM-DD and MM-DD formats
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    
    const seasonStart = marinaSettings.seasonStartDate || '05-01';
    const seasonEnd = marinaSettings.seasonEndDate || '09-30';
    
    // Handle seasonStart (might be YYYY-MM-DD or MM-DD)
    let startMonth, startDay;
    if (seasonStart.includes('-') && seasonStart.split('-').length === 3) {
      // YYYY-MM-DD format
      const [year, month, day] = seasonStart.split('-').map(Number);
      startMonth = month;
      startDay = day;
    } else {
      // MM-DD format
      const [month, day] = seasonStart.split('-').map(Number);
      startMonth = month;
      startDay = day;
    }
    
    // Handle seasonEnd (typically MM-DD)
    let endMonth, endDay;
    if (seasonEnd.includes('-') && seasonEnd.split('-').length === 3) {
      // YYYY-MM-DD format
      const [year, month, day] = seasonEnd.split('-').map(Number);
      endMonth = month;
      endDay = day;
    } else {
      // MM-DD format
      const [month, day] = seasonEnd.split('-').map(Number);
      endMonth = month;
      endDay = day;
    }
    
    const seasonStartDate = new Date(currentYear, startMonth - 1, startDay);
    const seasonEndDate = new Date(currentYear, endMonth - 1, endDay);
    
    const isInSeason = currentDate >= seasonStartDate && currentDate <= seasonEndDate;

    // Use the appropriate business hours based on season
    let businessHours;
    if (marinaSettings.isSeasonalHoursEnabled) {
      businessHours = isInSeason
        ? (marinaSettings.inSeasonHours || marinaSettings.businessHours)
        : (marinaSettings.outOfSeasonHours || marinaSettings.businessHours);
    } else {
      businessHours = marinaSettings.businessHours;
    }
    
    return businessHours || {};
  };

  const calculatePeriodRentableHours = (marinaSettings: any): number => {
    const businessHours = getActiveBusinessHours(marinaSettings);
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    // Calculate daily hours first
    let dailyHours = 0;
    let activeDays = 0;

    days.forEach((day) => {
      const dayHours = businessHours[day as keyof BusinessHours];
      
      if (dayHours && !dayHours.isClosed && dayHours.openTime && dayHours.closeTime) {
        try {
          const openTimeMinutes = convertTimeToMinutes(dayHours.openTime);
          const closeTimeMinutes = convertTimeToMinutes(dayHours.closeTime);
          
          if (closeTimeMinutes > openTimeMinutes) {
            const hoursThisDay = (closeTimeMinutes - openTimeMinutes) / 60; // Convert minutes to hours
            dailyHours += hoursThisDay;
            activeDays++;
          }
        } catch (error) {
          // Silently skip invalid time formats
        }
      }
    });
    
    const averageDailyHours = activeDays > 0 ? dailyHours / activeDays : 8;
    
    // Calculate total days in the selected period
    const daysDiff = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    const totalHours = timeFrame === 'today' ? averageDailyHours : 
                      timeFrame === 'week' ? dailyHours : 
                      averageDailyHours * daysDiff;
    
    // Fallback: If no business hours are configured, assume typical marina hours
    if (totalHours === 0) {
      setBusinessHoursWarning(true);
      return timeFrame === 'today' ? 8 : timeFrame === 'week' ? 56 : 8 * daysDiff;
    } else {
      setBusinessHoursWarning(false);
    }

    return totalHours;
  };

  const calculateReservedHours = (reservations: Reservation[]): number => {
    return reservations.reduce((total, reservation) => {
      if (reservation.timeSlot && reservation.timeSlot.includes('-')) {
        try {
          const [startTime, endTime] = reservation.timeSlot.split('-');
          const startMinutes = convertTimeToMinutes(startTime);
          const endMinutes = convertTimeToMinutes(endTime);
          return total + (endMinutes - startMinutes) / 60; // Convert minutes to hours
        } catch (error) {
          console.warn('Error parsing reservation time slot:', reservation.timeSlot, error);
          return total + 3; // Default 3-hour fallback
        }
      }
      // Fallback: try to parse from startTime and endTime fields
      if (reservation.startTime && reservation.endTime) {
        // This is a simple fallback - you might need to adjust based on your time format
        const duration = 3; // Default duration in hours
        return total + duration;
      }
      return total;
    }, 0);
  };

  const groupVesselsByType = (utilizationData: VesselUtilizationData[]): VesselTypeUtilizationData[] => {
    const typeGroups = new Map<string, VesselUtilizationData[]>();
    
    // Group vessels by type
    utilizationData.forEach(data => {
      const type = data.vessel.type || 'Unknown';
      if (!typeGroups.has(type)) {
        typeGroups.set(type, []);
      }
      typeGroups.get(type)!.push(data);
    });

    // Calculate aggregated metrics for each type
    const vesselTypeData: VesselTypeUtilizationData[] = Array.from(typeGroups.entries()).map(([type, vessels]) => {
      const totalPeriodRentableHours = vessels.reduce((sum, v) => sum + v.periodRentableHours, 0);
      const totalPeriodReservedHours = vessels.reduce((sum, v) => sum + v.periodReservedHours, 0);
      const totalReservations = vessels.reduce((sum, v) => sum + v.reservationCount, 0);
      const totalRevenue = vessels.reduce((sum, v) => sum + v.revenue, 0);
      
      const utilizationRate = totalPeriodRentableHours > 0 
        ? Math.round((totalPeriodReservedHours / totalPeriodRentableHours) * 100)
        : 0;

      return {
        vesselType: type,
        vesselCount: vessels.length,
        totalPeriodRentableHours,
        totalPeriodReservedHours,
        utilizationRate,
        totalReservations,
        totalRevenue,
        vessels: vessels.sort((a, b) => b.utilizationRate - a.utilizationRate)
      };
    });

    // Sort by utilization rate (highest first)
    return vesselTypeData.sort((a, b) => b.utilizationRate - a.utilizationRate);
  };

  const toggleTypeExpansion = (vesselType: string) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(vesselType)) {
      newExpanded.delete(vesselType);
    } else {
      newExpanded.add(vesselType);
    }
    setExpandedTypes(newExpanded);
  };

  const loadUtilizationData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [vessels, reservations, marinaSettingsData] = await Promise.all([
        getVessels(),
        getAllReservations(),
        getMarinaSettings()
      ]);

      // Store marina settings in state for date range calculations
      setMarinaSettings(marinaSettingsData);

      // Filter to only active vessels
      const activeVessels = vessels.filter(vessel => vessel.isActive && vessel.status === 'active');

      // Filter reservations to current period
      const periodStart = format(dateRange.start, 'yyyy-MM-dd');
      const periodEnd = format(dateRange.end, 'yyyy-MM-dd');
      
      const periodReservations = reservations.filter(reservation => {
        return reservation.date >= periodStart && 
               reservation.date <= periodEnd &&
               (reservation.status === 'confirmed' || reservation.status === 'pending');
      });

      // Calculate utilization data for each vessel
      const utilizationData: VesselUtilizationData[] = activeVessels.map(vessel => {
        // Get reservations for this vessel in the current period
        const vesselReservations = periodReservations.filter(res => res.vesselId === vessel.id);
        
        // Calculate hours using the full marina settings (handles seasonal hours)
        const dailyRentableHours = calculateDailyRentableHours(marinaSettingsData);
        const periodRentableHours = calculatePeriodRentableHours(marinaSettingsData);
        const periodReservedHours = calculateReservedHours(vesselReservations);
      
        // Calculate utilization rate
        const utilizationRate = periodRentableHours > 0 
          ? Math.round((periodReservedHours / periodRentableHours) * 100)
          : 0;

        // Calculate revenue
        const revenue = vesselReservations.reduce((total, res) => 
          total + (res.finalPrice || res.totalPrice || 0), 0
        );

        return {
          vessel,
          dailyRentableHours,
          periodRentableHours,
          periodReservedHours,
          utilizationRate,
          reservationCount: vesselReservations.length,
          revenue
        };
      });

      // Sort by utilization rate (highest first)
      utilizationData.sort((a, b) => b.utilizationRate - a.utilizationRate);
      
      setUtilizationData(utilizationData);
      setVesselTypeData(groupVesselsByType(utilizationData));
    } catch (err) {
      console.error('Error loading vessel utilization data:', err);
      setError('Failed to load vessel utilization data');
    } finally {
      setLoading(false);
    }
  };

  const getUtilizationColor = (rate: number): 'success' | 'warning' | 'error' | 'info' => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'info';
    if (rate >= 40) return 'warning';
    return 'error';
  };

  const overallStats = utilizationData.reduce(
    (acc, data) => ({
      totalRentableHours: acc.totalRentableHours + data.periodRentableHours,
      totalReservedHours: acc.totalReservedHours + data.periodReservedHours,
      totalRevenue: acc.totalRevenue + data.revenue,
      totalReservations: acc.totalReservations + data.reservationCount
    }),
    { totalRentableHours: 0, totalReservedHours: 0, totalRevenue: 0, totalReservations: 0 }
  );

  const overallUtilization = overallStats.totalRentableHours > 0 
    ? Math.round((overallStats.totalReservedHours / overallStats.totalRentableHours) * 100)
    : 0;

  if (loading) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Vessel Utilization
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Vessel Utilization
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">
          Vessel Utilization
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ButtonGroup size="small" variant="outlined">
            <Button 
              variant={timeFrame === 'today' ? 'contained' : 'outlined'}
              onClick={() => setTimeFrame('today')}
            >
              Today
            </Button>
            <Button 
              variant={timeFrame === 'week' ? 'contained' : 'outlined'}
              onClick={() => setTimeFrame('week')}
            >
              Week
            </Button>
            <Button 
              variant={timeFrame === 'month' ? 'contained' : 'outlined'}
              onClick={() => setTimeFrame('month')}
            >
              Month
            </Button>
            <Button 
              variant={timeFrame === 'season' ? 'contained' : 'outlined'}
              onClick={() => setTimeFrame('season')}
            >
              Season
            </Button>
            <Button 
              variant={timeFrame === 'last7' ? 'contained' : 'outlined'}
              onClick={() => setTimeFrame('last7')}
            >
              Last 7d
            </Button>
            <Button 
              variant={timeFrame === 'last30' ? 'contained' : 'outlined'}
              onClick={() => setTimeFrame('last30')}
            >
              Last 30d
            </Button>
          </ButtonGroup>
          <Tooltip title={`${getTimeFrameDisplayName()} data shown`}>
            <Chip 
              label={getTimeFrameLabel()}
              size="small"
              variant="outlined"
            />
          </Tooltip>
          <Button
            size="small"
            onClick={loadUtilizationData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Business Hours Warning */}
      {businessHoursWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Business hours are not properly configured. Using default hours for {getTimeFrameDisplayName().toLowerCase()} calculations. 
          Please check your business hours configuration in <strong>Admin → Settings → Business Hours</strong> for accurate utilization calculations.
        </Alert>
      )}

      {/* Overall Summary */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <Card variant="outlined" sx={{ 
            textAlign: 'center', 
            p: 1, 
            height: 100,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Typography variant="h4" color={overallUtilization >= 60 ? 'success.main' : 'warning.main'}>
              {overallUtilization}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Overall Utilization
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card variant="outlined" sx={{ 
            p: 1, 
            height: 100,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}>
            <Box sx={{ 
              flex: '0 0 auto',
              textAlign: 'center',
              minWidth: '50%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <Typography variant="h4" color="info.main">
                {overallStats.totalReservations}
              </Typography>
                          <Typography variant="caption" color="text.secondary">
              {getTimeFrameDisplayName()} Bookings
            </Typography>
            </Box>
            <Box sx={{ 
              flex: 1,
              pl: 1,
              borderLeft: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: '60px'
            }}>
              {vesselTypeData.length > 0 ? (
                <Box sx={{ px: 0.5 }}>
                  {vesselTypeData.slice(0, 3).map((typeData, index) => (
                    <Box key={typeData.vesselType} sx={{ display: 'flex', alignItems: 'center', mb: index < 2 ? 0.3 : 0 }}>
                      <Typography variant="caption" sx={{ textTransform: 'capitalize', fontSize: '0.7rem', flex: 1 }}>
                        {typeData.vesselType}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 'bold', ml: 1 }}>
                        {typeData.totalReservations}
                      </Typography>
                    </Box>
                  ))}
                  {vesselTypeData.length > 3 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.3, display: 'block', fontStyle: 'italic' }}>
                      +{vesselTypeData.length - 3} more...
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  No bookings
                </Typography>
              )}
            </Box>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card 
            variant="outlined" 
            sx={{ 
              p: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
              height: 100,
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Vessel Performance
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {vesselTypeData.length > 0 ? (
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: 1,
                  alignItems: 'center'
                }}>
                  {vesselTypeData.slice(0, 2).map((typeData, index) => (
                    <React.Fragment key={typeData.vesselType}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize', fontSize: '0.8rem' }}>
                        {typeData.vesselType}
                      </Typography>
                      <Chip 
                        label={`${typeData.utilizationRate}%`}
                        color={getUtilizationColor(typeData.utilizationRate)}
                        size="small"
                        sx={{ 
                          height: 18, 
                          fontSize: '0.65rem',
                          width: 36,
                          '& .MuiChip-label': {
                            px: 0.5
                          }
                        }}
                      />
                                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
                          {Math.round(typeData.totalPeriodReservedHours)}/{Math.round(typeData.totalPeriodRentableHours)}h
                        </Typography>
                    </React.Fragment>
                  ))}
                  {vesselTypeData.length > 2 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', gridColumn: '1 / -1', mt: 0.5 }}>
                      +{vesselTypeData.length - 2} more types...
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No vessel data
                </Typography>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Vessel Types */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mb: 2 }}>
          {vesselTypeData.map((typeData) => (
            <Card key={typeData.vesselType} variant="outlined" sx={{ mb: 2 }}>
              <CardContent 
                sx={{ 
                  p: 2, 
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => toggleTypeExpansion(typeData.vesselType)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VesselIcon color="primary" />
                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                      {typeData.vesselType}
                    </Typography>
                    <Chip 
                      label={`${typeData.vesselCount} vessel${typeData.vesselCount !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip 
                      label={`${typeData.utilizationRate}%`}
                      color={getUtilizationColor(typeData.utilizationRate)}
                      size="small"
                    />
                    <IconButton size="small">
                      {expandedTypes.has(typeData.vesselType) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(typeData.utilizationRate, 100)} 
                    color={getUtilizationColor(typeData.utilizationRate)}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Utilization
                    </Typography>
                    <Typography variant="body2">
                                                {Math.round(typeData.totalPeriodReservedHours)}h / {Math.round(typeData.totalPeriodRentableHours)}h
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Bookings
                    </Typography>
                    <Typography variant="body2">
                      {typeData.totalReservations}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Revenue
                    </Typography>
                    <Typography variant="body2">
                      ${typeData.totalRevenue.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Avg per Vessel
                    </Typography>
                    <Typography variant="body2">
                      {Math.round(typeData.utilizationRate)}% utilization
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
              
              {/* Individual Vessels for this Type */}
              <Collapse in={expandedTypes.has(typeData.vesselType)} timeout="auto" unmountOnExit>
                <Box sx={{ p: 2, pt: 0 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Individual {typeData.vesselType} Performance
                  </Typography>
                  <Grid container spacing={2}>
                    {typeData.vessels.map((vesselData) => (
                      <Grid item xs={12} sm={6} md={4} key={vesselData.vessel.id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            cursor: onVesselClick ? 'pointer' : 'default',
                            '&:hover': onVesselClick ? {
                              boxShadow: 2,
                              transform: 'translateY(-1px)',
                            } : {}
                          }}
                          onClick={() => onVesselClick?.(vesselData.vessel.id)}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <VesselIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                                {vesselData.vessel.name}
                              </Typography>
                              <Chip 
                                label={`${vesselData.utilizationRate}%`}
                                color={getUtilizationColor(vesselData.utilizationRate)}
                                size="small"
                              />
                            </Box>
                            
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              {vesselData.vessel.length}ft • ID: {vesselData.vessel.id?.slice(-6)}
                            </Typography>
                            
                            <Box sx={{ mt: 1, mb: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={Math.min(vesselData.utilizationRate, 100)} 
                                color={getUtilizationColor(vesselData.utilizationRate)}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary">
                              {Math.round(vesselData.periodReservedHours)}h / {Math.round(vesselData.periodRentableHours)}h
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {vesselData.reservationCount} bookings
                              </Typography>
                              <Typography variant="caption" color="success.main">
                                ${vesselData.revenue.toLocaleString()}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Collapse>
            </Card>
          ))}
        </Box>
        
        {vesselTypeData.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <VesselIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No active vessels found for the selected week.
            </Typography>
          </Box>
        )}
      </Collapse>
      
      {!expanded && vesselTypeData.length > 0 && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Click the Vessel Types card above to view detailed breakdown
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default VesselUtilizationCard; 